import {
  QueryCache,
  MutationCache,
  QueryClient,
  type QueryCacheNotifyEvent,
} from "@tanstack/react-query";

import * as Sentry from "@sentry/nextjs";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, // All queries will retry 1 times by default
    },
  },
  // another way to handle errors is to tap into defaultOptions.queries.throwOnError - seems to basically behave the same, having a har time finding the best practice in the docs.
  // defaultOptions: {
  // queries: {
  //   throwOnError: (error, query) => {
  //     const payload = {
  //       tags: {
  //         errorSource: "tanstack-query",
  //         statusCode: (
  //           query.state.error as any
  //         )?.response?.status?.toString(),
  //         queryKey: JSON.stringify(query.queryKey),
  //       },
  //       extra: {
  //         timestamp: new Date().toISOString(),
  //         queryInfo: {
  //           queryKey: query.queryKey,
  //           queryHash: query.queryHash,
  //           fetchFailureCount: query.state.fetchFailureCount,
  //         },
  //         graphqlQuery: (query.state.error as any).request?.query,
  //         error: error.message,
  //       },
  //     };
  //     Sentry.captureException(error, payload);
  //     return false; // maintaining the default here which is to NOT throw the error to the error boundary
  //   },
  // },
  // },
});

// Subscribe to the query cache to capture errors globally
queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
  // here we want to capture errors that are 400s - they have a different shape than the errors that come from a 200
  // this is equivalent to the throwOnError option above...
  if (event?.type === "observerResultsUpdated" || event?.type === "updated") {
    const error = event?.query?.state?.error;
    const statusCode = error?.response?.status?.toString();
    if (error && statusCode === "400") {
      const sentryError = new Error(`TanStack Query Error: ${error}`);

      const payload = {
        tags: {
          errorSource: "tanstack-query",
          statusCode,
          queryKey: JSON.stringify(event?.query?.queryKey),
        },
        extra: {
          timestamp: new Date().toISOString(),
          queryInfo: {
            queryKey: event?.query?.queryKey,
            queryHash: event?.query?.queryHash,
          },
          error,
        },
      };

      Sentry.captureException(sentryError, payload);
    }
  }

  // the only event action that seems to have an error is the event type of "updated"
  // the is catching the error from our graphql API when they succeed but have an error in the response.
  if (event?.type === "updated" && event?.action?.type === "error") {
    const error = event?.action?.error?.response?.errors?.[0];
    if (error) {
      // Create a more descriptive error with the original message
      const sentryError = new Error(error.message);

      // Add the original error stack if available
      if (event?.action?.error?.stack) {
        sentryError.stack = event.action.error.stack;
      } else if (error.extensions?.stacktrace) {
        // Use the GraphQL stacktrace if available
        sentryError.stack = error.extensions.stacktrace.join("\n");
      }

      const payload = {
        tags: {
          errorSource: "tanstack-query",
          graphqlPath: error.path?.join("."),
          statusCode:
            error.extensions?.status?.toString() ||
            error.extensions?.originalError?.statusCode?.toString(),
          errorCode: error?.extensions?.code,
        },
        extra: {
          timestamp: new Date().toISOString(),
          queryInfo: {
            queryKey: event?.query?.queryKey,
            queryHash: event?.query?.queryHash,
          },
          error,
        },
      };

      Sentry.captureException(sentryError, payload);
    }
  }
});
